import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import TuneIcon from "@mui/icons-material/Tune";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import { useParams } from "react-router-dom";
import { apiClient } from "@/services/api";
// 导入项目自评指标导入对话框组件
import ProjectSelfEvaluationIndicatorAIImportDialog from '@/components/indicators/ProjectSelfEvaluationIndicatorAIImportDialog';
import ProjectSelfEvaluationIndicatorSortDialog from '@/components/indicators/ProjectSelfEvaluationIndicatorSortDialog';
import UploadedDocumentsDialog from './UploadedDocumentsDialog';

interface FundManagement {
  id?: number;
  project_id?: number;
  evaluation_item: string;
  evaluation_content: string;
  situation_description: string;
  score_value: string;
  score: string;
  problems_and_improvements: string;
  sort_order: number;
}

interface ProjectSelfEvaluationIndicator {
  id: number;
  project_id: number;
  level1_code: string;
  level1_name: string;
  level2_code: string;
  level2_name: string;
  level3_code: string;
  level3_name: string;
  indicator_value: string;
  value_description: string;
  actual_completion: string; // 实际完成情况
  score_value: string; // 分值
  score: string; // 得分
  deviation_degree: string; // 偏差度%
  deviation_reason: string; // 偏差原因分析及改进措施
  completion_probability: string;
  note: string;
  ai_recognition_basis: string;
  created_at: string;
  updated_at: string;
}

interface PerformanceMeta {
  department_name: string;
  unit_name: string;
  annual_target: string;
}

interface FundExecution {
  id?: number;
  fund_type: string;
  initial_budget: string;
  adjusted_budget: string;
  full_year_budget: string;
  execution_1_7: string;
  execution_1_7_rate: string;
  full_year_execution: string;
  execution_rate: string;
  expected_full_year: string;
  expected_full_year_rate: string;
  score_value: string;
  score: string;
  sort_order: number;
}

function getDefaultFundExecutions(): FundExecution[] {
  return [
    {
      fund_type: "年度资金总额",
      initial_budget: "",
      adjusted_budget: "",
      full_year_budget: "",
      execution_1_7: "",
      execution_1_7_rate: "",
      full_year_execution: "",
      execution_rate: "",
      expected_full_year: "",
      expected_full_year_rate: "",
      score_value: "",
      score: "",
      sort_order: 0,
    },
    {
      fund_type: "其中:财政拨款(政府预算资金)",
      initial_budget: "",
      adjusted_budget: "",
      full_year_budget: "",
      execution_1_7: "",
      execution_1_7_rate: "",
      full_year_execution: "",
      execution_rate: "",
      expected_full_year: "",
      expected_full_year_rate: "",
      score_value: "",
      score: "",
      sort_order: 1,
    },
    {
      fund_type: "财政专户管理资金",
      initial_budget: "",
      adjusted_budget: "",
      full_year_budget: "",
      execution_1_7: "",
      execution_1_7_rate: "",
      full_year_execution: "",
      execution_rate: "",
      expected_full_year: "",
      expected_full_year_rate: "",
      score_value: "",
      score: "",
      sort_order: 2,
    },
    {
      fund_type: "单位资金",
      initial_budget: "",
      adjusted_budget: "",
      full_year_budget: "",
      execution_1_7: "",
      execution_1_7_rate: "",
      full_year_execution: "",
      execution_rate: "",
      expected_full_year: "",
      expected_full_year_rate: "",
      score_value: "",
      score: "",
      sort_order: 3,
    },
  ];
}

function getDefaultFundManagements(): FundManagement[] {
  return [
    {
      evaluation_item: "安排科学性",
      evaluation_content: "",
      situation_description: "",
      score_value: "",
      score: "",
      problems_and_improvements: "",
      sort_order: 0,
    },
    {
      evaluation_item: "支付合规性",
      evaluation_content: "",
      situation_description: "",
      score_value: "",
      score: "",
      problems_and_improvements: "",
      sort_order: 1,
    },
    {
      evaluation_item: "使用规范性",
      evaluation_content: "",
      situation_description: "",
      score_value: "",
      score: "",
      problems_and_improvements: "",
      sort_order: 2,
    },
    {
      evaluation_item: "预算绩效管理",
      evaluation_content: "",
      situation_description: "",
      score_value: "",
      score: "",
      problems_and_improvements: "",
      sort_order: 3,
    },
  ];
}

const ProjectSelfEvaluationIndicatorManager: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIndicatorIds, setSelectedIndicatorIds] = useState<number[]>([]);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [isExporting, setIsExporting] = useState(false);

  // 批量删除相关状态
  const [batchDeleteConfirmOpen, setBatchDeleteConfirmOpen] = useState(false);
  const [isBatchDeleting, setIsBatchDeleting] = useState(false);

  // 导入对话框状态
  const [aiImportDialogOpen, setAiImportDialogOpen] = useState(false);

  // 一键计算得分状态
  const [isCalculatingScore, setIsCalculatingScore] = useState(false);
  const [scoreResult, setScoreResult] = useState<any>(null);
  const [scoreResultDialogOpen, setScoreResultDialogOpen] = useState(false);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addDialogError, setAddDialogError] = useState<string | null>(null);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addForm, setAddForm] = useState({
    level1_name: "",
    level2_name: "",
    level3_name: "",
    indicator_value: "",
    value_description: "",
    score_value: "",
    score: "",
    deviation_degree: "",
    deviation_reason: "",
    completion_probability: "",
    note: "",
  });

  const resetAddForm = () => {
    setAddForm({
      level1_name: "",
      level2_name: "",
      level3_name: "",
      indicator_value: "",
      value_description: "",
      score_value: "",
      score: "",
      deviation_degree: "",
      deviation_reason: "",
      completion_probability: "",
      note: "",
    });
    setAddDialogError(null);
  };

  const handleAddIndicator = async () => {
    if (!projectId) return;
    if (!addForm.level3_name?.trim() || !addForm.indicator_value?.trim()) {
      setAddDialogError("三级指标名称和指标值为必填项");
      return;
    }
    setAddSubmitting(true);
    setAddDialogError(null);
    try {
      const resp = await apiClient.post(
        `/projects/${projectId}/project-self-evaluation-indicators`,
        addForm
      );
      if (resp?.success) {
        setAddDialogOpen(false);
        resetAddForm();
        await fetchIndicators();
      } else {
        setAddDialogError(resp?.message || "添加失败");
      }
    } catch (e: any) {
      setAddDialogError(e?.response?.data?.detail || e?.message || "添加失败");
    } finally {
      setAddSubmitting(false);
    }
  };

  const [project, setProject] = useState<any>(null);
  const [meta, setMeta] = useState<PerformanceMeta>({
    department_name: "",
    unit_name: "",
    annual_target: "",
  });
  const [savingMeta, setSavingMeta] = useState(false);
  const [fundExecutions, setFundExecutions] = useState<FundExecution[]>(
    getDefaultFundExecutions()
  );
  const [indicators, setIndicators] = useState<ProjectSelfEvaluationIndicator[]>(
    []
  );
  const [fundManagements, setFundManagements] = useState<FundManagement[]>([]);

  // 指标排序对话框状态
  const [sortDialogOpen, setSortDialogOpen] = useState(false);
  const [isSorting, setIsSorting] = useState(false);

  // 文档管理弹窗状态
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);

  const fetchProject = useCallback(async () => {
    if (!projectId) return;
    try {
      const resp = await apiClient.get(`/projects/${projectId}`);
      if (resp?.success && resp.data) setProject(resp.data);
    } catch (e) {
      // ignore: 项目信息可缺省显示
      console.error("获取项目信息失败", e);
    }
  }, [projectId]);

  const fetchMeta = useCallback(async () => {
    if (!projectId) return;
    try {
      const resp = await apiClient.get(`/projects/${projectId}/performance-meta`);
      if (resp?.success && resp.data) {
        setMeta({
          department_name: resp.data.department_name || "",
          unit_name: resp.data.unit_name || "",
          annual_target: resp.data.annual_target || "",
        });
      }
    } catch (e) {
      // ignore: 元信息可缺省显示
      console.error("获取项目元信息失败", e);
    }
  }, [projectId]);

  const fetchFundExecutions = useCallback(async () => {
    if (!projectId) return;
    try {
      const resp = await apiClient.get(`/projects/${projectId}/fund-executions`);
      const items = resp?.data?.items;
      if (resp?.success && Array.isArray(items)) {
        if (items.length === 0) setFundExecutions(getDefaultFundExecutions());
        else setFundExecutions(items);
      } else {
        setFundExecutions(getDefaultFundExecutions());
      }
    } catch (e) {
      console.error("获取资金执行情况失败", e);
      setFundExecutions(getDefaultFundExecutions());
    }
  }, [projectId]);

  const fetchFundManagements = useCallback(async () => {
    if (!projectId) return;
    try {
      const resp = await apiClient.get(`/projects/${projectId}/fund-management`);
      const items = resp?.data?.items;
      if (resp?.success && Array.isArray(items)) {
        if (items.length === 0) setFundManagements(getDefaultFundManagements());
        else setFundManagements(items);
      } else {
        setFundManagements(getDefaultFundManagements());
      }
    } catch (e) {
      console.error("获取资金管理情况失败", e);
      setFundManagements(getDefaultFundManagements());
    }
  }, [projectId]);

  const handleSaveFundManagements = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await apiClient.post(`/projects/${projectId}/fund-management/batch-update`, fundManagements);
      if (resp?.success) {
        // 保存成功
      } else {
        setError(resp?.message || "保存资金管理情况失败");
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "保存资金管理情况失败");
    } finally {
      setLoading(false);
    }
  }, [projectId, fundManagements]);

  const handleSaveMeta = useCallback(async () => {
    if (!projectId) return;
    setSavingMeta(true);
    setError(null);
    try {
      const resp = await apiClient.put(`/projects/${projectId}/performance-meta`, meta);
      if (resp?.success) {
        // 可选：短暂提示成功
      } else {
        setError(resp?.message || "保存失败");
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "保存失败");
    } finally {
      setSavingMeta(false);
    }
  }, [projectId, meta]);

  const fetchIndicators = useCallback(async () => {
    if (!projectId) return;
    try {
      const resp = await apiClient.get(
        `/projects/${projectId}/project-self-evaluation-indicators?page=1&page_size=1000`
      );
      if (resp?.success && Array.isArray(resp.data)) {
        setIndicators(resp.data);
      } else {
        setIndicators([]);
      }
    } catch (e: any) {
      console.error("获取项目自评指标失败", e);
      throw e;
    }
  }, [projectId]);

  const handleImportSuccess = async () => {
    await fetchIndicators();
    await fetchFundManagements();
  };

  // 导出为Excel
  const handleExportExcel = async () => {
    if (!projectId) return;

    setExportMenuAnchor(null);
    setIsExporting(true);
    try {
      const resp = await fetch(`/api/v1/projects/${projectId}/project-self-evaluation-indicators/export-excel`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`
        }
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || err.error || '导出失败');
      }

      // 从响应头中提取文件名
      const contentDisposition = resp.headers.get('Content-Disposition') || '';
      const projectName = project?.project_name || '项目';
      let filename = `${projectName}_项目自评指标表.xlsx`;

      const filenameMatch = contentDisposition.match(/filename\*=UTF-8''(.+)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = decodeURIComponent(filenameMatch[1]);
      } else {
        const filenameMatch2 = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch2 && filenameMatch2[1]) {
          filename = filenameMatch2[1];
        }
      }

      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      setError(error.message || '导出失败');
    } finally {
      setIsExporting(false);
    }
  };

  // 导出为Word
  const handleExportWord = async () => {
    if (!projectId) return;

    setExportMenuAnchor(null);
    setIsExporting(true);
    try {
      const resp = await fetch(`/api/v1/projects/${projectId}/project-self-evaluation-indicators/export-word`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`
        }
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || err.error || '导出失败');
      }

      // 从响应头中提取文件名
      const contentDisposition = resp.headers.get('Content-Disposition') || '';
      const projectName = project?.project_name || '项目';
      let filename = `${projectName}_项目自评指标表.docx`;

      const filenameMatch = contentDisposition.match(/filename\*=UTF-8''(.+)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = decodeURIComponent(filenameMatch[1]);
      } else {
        const filenameMatch2 = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch2 && filenameMatch2[1]) {
          filename = filenameMatch2[1];
        }
      }

      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      setError(error.message || '导出失败');
    } finally {
      setIsExporting(false);
    }
  };

  const handleOpenSortDialog = () => {
    if (indicators.length === 0) return;
    setSortDialogOpen(true);
  };

  const handleCloseSortDialog = () => {
    setSortDialogOpen(false);
  };

  const handleSaveSort = async (newCodes: any[]) => {
    if (!projectId) return;
    setIsSorting(true);
    setError(null);
    try {
      const resp = await apiClient.post(
        `/projects/${projectId}/project-self-evaluation-indicators/reorder`,
        { newCodes }
      );
      if (resp?.success) {
        await fetchIndicators();
        setSortDialogOpen(false);
      } else {
        setError(resp?.message || "排序保存失败");
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "排序保存失败");
    } finally {
      setIsSorting(false);
    }
  };

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        await Promise.all([
          fetchProject(),
          fetchMeta(),
          fetchFundExecutions(),
          fetchFundManagements(),
          fetchIndicators(),
        ]);
      } catch (e: any) {
        setError(e?.response?.data?.detail || e?.message || "页面数据加载失败");
      } finally {
        setLoading(false);
      }
    })();
  }, [projectId, fetchProject, fetchMeta, fetchFundExecutions, fetchFundManagements, fetchIndicators]);

  const sortedIndicators = useMemo(() => {
    return [...indicators].sort((a, b) =>
      (a.level3_code || "").localeCompare(b.level3_code || "")
    );
  }, [indicators]);

  // 选择单个指标进行批量删除
  const handleSelectIndicatorForBatchDelete = (indicatorId: number, checked: boolean) => {
    if (checked) {
      setSelectedIndicatorIds((prev) => [...prev, indicatorId]);
    } else {
      setSelectedIndicatorIds((prev) => prev.filter((id) => id !== indicatorId));
    }
  };

  // 全选/取消全选指标进行批量删除
  const handleSelectAllIndicatorsForBatchDelete = (checked: boolean) => {
    if (checked) {
      setSelectedIndicatorIds(indicators.map((indicator) => indicator.id));
    } else {
      setSelectedIndicatorIds([]);
    }
  };

  // 打开批量删除确认对话框
  const openBatchDeleteConfirmDialog = () => {
    if (selectedIndicatorIds.length === 0) return;
    setBatchDeleteConfirmOpen(true);
  };

  // 关闭批量删除确认对话框
  const closeBatchDeleteConfirmDialog = () => {
    setBatchDeleteConfirmOpen(false);
  };

  // 批量删除确认
  const handleConfirmBatchDelete = async () => {
    if (selectedIndicatorIds.length === 0) return;

    setIsBatchDeleting(true);
    setError(null);

    try {
      const response = await apiClient.post(
        `/projects/${projectId}/project-self-evaluation-indicators/batch-delete`,
        {
          indicator_ids: selectedIndicatorIds,
        }
      );

      if (response.success) {
        setSelectedIndicatorIds([]);
        await fetchIndicators(); // 刷新列表
        closeBatchDeleteConfirmDialog();
      } else {
        throw new Error(response.error || "批量删除失败");
      }
    } catch (err: any) {
      setError(err.message || "批量删除失败");
    } finally {
      setIsBatchDeleting(false);
    }
  };

  // 一键计算得分
  const handleCalculateScore = async () => {
    if (!projectId) return;

    setIsCalculatingScore(true);
    setError(null);

    try {
      const response = await apiClient.post(
        `/projects/${projectId}/project-self-evaluation-score/calculate`
      );

      if (response.success) {
        setScoreResult(response.data);
        setScoreResultDialogOpen(true);
        // 刷新数据
        await Promise.all([
          fetchIndicators(),
          fetchFundExecutions(),
          fetchFundManagements(),
        ]);
      } else {
        setError(response.message || "计算得分失败");
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || "计算得分失败");
    } finally {
      setIsCalculatingScore(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        {/* 标题和操作栏：与事中监控一致 */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h5" component="h1">
            项目自评指标管理
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => {
                resetAddForm();
                setAddDialogOpen(true);
              }}
            >
              添加指标
            </Button>
            <Button
              variant="outlined"
              startIcon={<FolderOpenIcon />}
              onClick={() => setDocumentsDialogOpen(true)}
            >
              文档管理
            </Button>
            <Button
              variant="outlined"
              startIcon={<TuneIcon />}
              onClick={handleOpenSortDialog}
              disabled={indicators.length === 0 || isSorting}
            >
              指标排序
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AutoAwesomeIcon />}
              onClick={handleCalculateScore}
              disabled={isCalculatingScore}
            >
              {isCalculatingScore ? "计算中..." : "一键计算得分"}
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileUploadIcon />}
              onClick={() => setAiImportDialogOpen(true)}
            >
              导入
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={(e) => setExportMenuAnchor(e.currentTarget)}
              disabled={indicators.length === 0 || isExporting}
            >
              {isExporting ? '导出中...' : '导出'}
            </Button>
            <Menu
              anchorEl={exportMenuAnchor}
              open={Boolean(exportMenuAnchor)}
              onClose={() => setExportMenuAnchor(null)}
            >
              <MenuItem onClick={handleExportExcel} disabled={isExporting}>
                {isExporting ? '导出中...' : '导出为 Excel (.xlsx)'}
              </MenuItem>
              <MenuItem onClick={handleExportWord} disabled={isExporting}>
                {isExporting ? '导出中...' : '导出为 Word (.docx)'}
              </MenuItem>
            </Menu>
          </Stack>
        </Box>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* 项目信息 + 资金：与事中监控同一块 Paper、同一间距与字体 */}
        <Paper sx={{ p: 1.5, mb: 2 }} variant="outlined">
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
              项目信息
            </Typography>
            <Button
              size="small"
              variant="contained"
              onClick={handleSaveMeta}
              disabled={savingMeta}
            >
              {savingMeta ? "保存中..." : "保存项目信息"}
            </Button>
          </Box>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <TextField
                label="项目名称"
                fullWidth
                size="small"
                value={project?.project_name || ""}
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="部门名称"
                fullWidth
                size="small"
                value={meta.department_name || ""}
                onChange={(e) => setMeta({ ...meta, department_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="单位名称"
                fullWidth
                size="small"
                value={meta.unit_name || ""}
                onChange={(e) => setMeta({ ...meta, unit_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="年度目标"
                fullWidth
                multiline
                rows={2}
                size="small"
                value={meta.annual_target || ""}
                onChange={(e) => setMeta({ ...meta, annual_target: e.target.value })}
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
            项目资金(万元)
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead sx={{ backgroundColor: "action.hover" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", width: 200 }}>项目资金</TableCell>
                  <TableCell sx={{ fontWeight: "bold", minWidth: 100 }}>年初预算数</TableCell>
                  <TableCell sx={{ fontWeight: "bold", minWidth: 100 }}>全年预算数</TableCell>
                  <TableCell sx={{ fontWeight: "bold", minWidth: 120 }}>全年执行数</TableCell>
                  <TableCell sx={{ fontWeight: "bold", minWidth: 100 }}>执行率 %</TableCell>
                  <TableCell sx={{ fontWeight: "bold", minWidth: 80 }}>分值</TableCell>
                  <TableCell sx={{ fontWeight: "bold", minWidth: 80 }}>得分</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fundExecutions.map((exec, index) => {
                  const initialBudget = parseFloat(exec.initial_budget || "0");
                  const fullYearBudget = parseFloat(exec.full_year_budget || "0");
                  const fullYearExecution = parseFloat(exec.full_year_execution || "0");
                  const executionRate =
                    initialBudget > 0 ? ((fullYearExecution / initialBudget) * 100).toFixed(2) + "%" :
                    fullYearBudget > 0 ? ((fullYearExecution / fullYearBudget) * 100).toFixed(2) + "%" : "-";
                  return (
                    <TableRow key={exec.id ?? index}>
                      <TableCell sx={{ fontWeight: "bold" }}>{exec.fund_type}</TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          value={exec.initial_budget || ""}
                          onChange={(e) => {
                            const updated = [...fundExecutions];
                            updated[index].initial_budget = e.target.value;
                            setFundExecutions(updated);
                          }}
                          placeholder="年初预算数"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          value={exec.full_year_budget || ""}
                          onChange={(e) => {
                            const updated = [...fundExecutions];
                            updated[index].full_year_budget = e.target.value;
                            setFundExecutions(updated);
                          }}
                          placeholder="全年预算数"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          value={exec.full_year_execution || ""}
                          onChange={(e) => {
                            const updated = [...fundExecutions];
                            updated[index].full_year_execution = e.target.value;
                            setFundExecutions(updated);
                          }}
                          placeholder="全年执行数"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{executionRate}</Typography>
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          value={exec.score_value || ""}
                          onChange={(e) => {
                            const updated = [...fundExecutions];
                            updated[index].score_value = e.target.value;
                            setFundExecutions(updated);
                          }}
                          placeholder="分值"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          value={exec.score || ""}
                          onChange={(e) => {
                            const updated = [...fundExecutions];
                            updated[index].score = e.target.value;
                            setFundExecutions(updated);
                          }}
                          placeholder="得分"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1, mt: 2 }}>
            资金管理情况
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead sx={{ backgroundColor: "action.hover" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", width: 150 }}>评价项目</TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: 150 }}>情况说明</TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: 80 }}>分值</TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: 80 }}>得分</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>存在问题和改进措施</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fundManagements.map((item, index) => (
                  <TableRow key={item.id ?? index}>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      <Typography variant="body2">{item.evaluation_item || "-"}</Typography>
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={item.situation_description || ""}
                        onChange={(e) => {
                          const updated = [...fundManagements];
                          updated[index].situation_description = e.target.value;
                          setFundManagements(updated);
                        }}
                        placeholder="情况说明"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={item.score_value || ""}
                        onChange={(e) => {
                          const updated = [...fundManagements];
                          updated[index].score_value = e.target.value;
                          setFundManagements(updated);
                        }}
                        placeholder="分值"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={item.score || ""}
                        onChange={(e) => {
                          const updated = [...fundManagements];
                          updated[index].score = e.target.value;
                          setFundManagements(updated);
                        }}
                        placeholder="得分"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        multiline
                        rows={2}
                        value={item.problems_and_improvements || ""}
                        onChange={(e) => {
                          const updated = [...fundManagements];
                          updated[index].problems_and_improvements = e.target.value;
                          setFundManagements(updated);
                        }}
                        placeholder="存在问题和改进措施"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ mt: 1, textAlign: "right" }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleSaveFundManagements}
              disabled={loading}
            >
              保存资金管理情况
            </Button>
          </Box>
        </Paper>

        {/* 指标表格：与事中监控一致 TableContainer 样式与列宽 */}
        {!loading && (
          <>
            {selectedIndicatorIds.length > 0 && (
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={openBatchDeleteConfirmDialog}
                >
                  批量删除 ({selectedIndicatorIds.length})
                </Button>
              </Box>
            )}
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: "70vh", overflowX: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead sx={{ backgroundColor: "action.hover" }}>
                <TableRow>
                  <TableCell
                    sx={{ fontWeight: "bold", width: 48, minWidth: 48, maxWidth: 48, padding: "8px" }}
                    padding="checkbox"
                  >
                    <Checkbox
                      checked={indicators.length > 0 && selectedIndicatorIds.length === indicators.length}
                      indeterminate={selectedIndicatorIds.length > 0 && selectedIndicatorIds.length < indicators.length}
                      onChange={(e) => handleSelectAllIndicatorsForBatchDelete(e.target.checked)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: 60, minWidth: 60, maxWidth: 60 }}>序号</TableCell>
                  <TableCell sx={{ fontWeight: "bold", minWidth: 100 }}>一级指标</TableCell>
                  <TableCell sx={{ fontWeight: "bold", minWidth: 120 }}>二级指标</TableCell>
                  <TableCell sx={{ fontWeight: "bold", minWidth: 150 }}>三级指标</TableCell>
                  <TableCell sx={{ fontWeight: "bold", minWidth: 100 }}>指标值</TableCell>
                  <TableCell sx={{ fontWeight: "bold", minWidth: 120 }}>指标值说明</TableCell>
                  <TableCell sx={{ fontWeight: "bold", minWidth: 100 }}>实际完成情况</TableCell>
                  <TableCell sx={{ fontWeight: "bold", minWidth: 80 }}>分值</TableCell>
                  <TableCell sx={{ fontWeight: "bold", minWidth: 80 }}>得分</TableCell>
                  <TableCell sx={{ fontWeight: "bold", minWidth: 100 }}>偏差度%</TableCell>
                  <TableCell sx={{ fontWeight: "bold", minWidth: 200 }}>偏差原因分析及改进措施</TableCell>
                  <TableCell sx={{ fontWeight: "bold", minWidth: 200 }}>AI识别依据</TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: 120, minWidth: 120, maxWidth: 120, textAlign: "center" }}>
                    操作
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedIndicators.map((it, index) => (
                  <TableRow key={it.id} hover>
                    <TableCell
                      padding="checkbox"
                      sx={{ width: 48, minWidth: 48, maxWidth: 48, padding: "8px" }}
                    >
                      <Checkbox
                        checked={selectedIndicatorIds.includes(it.id)}
                        onChange={(e) => handleSelectIndicatorForBatchDelete(it.id, e.target.checked)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ width: 60, minWidth: 60, maxWidth: 60 }}>{index + 1}</TableCell>
                    <TableCell>{it.level1_name}</TableCell>
                    <TableCell>{it.level2_name}</TableCell>
                    <TableCell>{it.level3_name}</TableCell>
                    <TableCell>{it.indicator_value}</TableCell>
                    <TableCell sx={{ whiteSpace: "pre-wrap" }}>{it.value_description || "-"}</TableCell>
                    <TableCell sx={{ whiteSpace: "pre-wrap" }}>{it.actual_completion || "-"}</TableCell>
                    <TableCell>{it.score_value || "-"}</TableCell>
                    <TableCell>{it.score || "-"}</TableCell>
                    <TableCell>{it.deviation_degree || "-"}</TableCell>
                    <TableCell sx={{ whiteSpace: "pre-wrap" }}>{it.deviation_reason || "-"}</TableCell>
                    <TableCell>
                      <Tooltip
                        title={<Box sx={{ whiteSpace: "pre-wrap", maxWidth: 400 }}>{it.ai_recognition_basis || "-"}</Box>}
                        placement="top"
                        arrow
                      >
                        <Box
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: 200,
                            cursor: it.ai_recognition_basis ? "pointer" : "default"
                          }}
                        >
                          {it.ai_recognition_basis || "-"}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center" sx={{ width: 120, minWidth: 120, maxWidth: 120 }}>
                      <Tooltip title="编辑">
                        <IconButton size="small" onClick={() => {}}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="删除">
                        <IconButton size="small" onClick={() => {}} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          </>
        )}

        {!loading && sortedIndicators.length === 0 && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              暂无项目自评指标数据
            </Typography>
          </Box>
        )}
      </Paper>

      {/* 添加指标对话框 */}
      <Dialog
        open={addDialogOpen}
        onClose={() => { setAddDialogOpen(false); setAddDialogError(null); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>添加项目自评指标</DialogTitle>
        <DialogContent>
          {addDialogError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAddDialogError(null)}>
              {addDialogError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="一级指标名称"
                fullWidth
                size="small"
                value={addForm.level1_name}
                onChange={(e) => setAddForm({ ...addForm, level1_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="二级指标名称"
                fullWidth
                size="small"
                value={addForm.level2_name}
                onChange={(e) => setAddForm({ ...addForm, level2_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="三级指标名称 *"
                fullWidth
                size="small"
                value={addForm.level3_name}
                onChange={(e) => setAddForm({ ...addForm, level3_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="指标值 *"
                fullWidth
                size="small"
                value={addForm.indicator_value}
                onChange={(e) => setAddForm({ ...addForm, indicator_value: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="指标值说明"
                fullWidth
                size="small"
                multiline
                rows={2}
                value={addForm.value_description}
                onChange={(e) => setAddForm({ ...addForm, value_description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="分值"
                fullWidth
                size="small"
                value={addForm.score_value}
                onChange={(e) => setAddForm({ ...addForm, score_value: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="得分"
                fullWidth
                size="small"
                value={addForm.score}
                onChange={(e) => setAddForm({ ...addForm, score: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="偏差度%"
                fullWidth
                size="small"
                value={addForm.deviation_degree}
                onChange={(e) => setAddForm({ ...addForm, deviation_degree: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="偏差原因分析及改进措施"
                fullWidth
                size="small"
                multiline
                rows={2}
                value={addForm.deviation_reason}
                onChange={(e) => setAddForm({ ...addForm, deviation_reason: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="完成目标可能性"
                fullWidth
                size="small"
                value={addForm.completion_probability}
                onChange={(e) => setAddForm({ ...addForm, completion_probability: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="备注"
                fullWidth
                size="small"
                multiline
                rows={2}
                value={addForm.note}
                onChange={(e) => setAddForm({ ...addForm, note: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAddDialogOpen(false); setAddDialogError(null); }}>
            取消
          </Button>
          <Button onClick={handleAddIndicator} variant="contained" disabled={addSubmitting}>
            {addSubmitting ? "提交中..." : "添加"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI智能导入对话框 */}
      <ProjectSelfEvaluationIndicatorAIImportDialog
        open={aiImportDialogOpen}
        onClose={() => setAiImportDialogOpen(false)}
        projectId={projectId || ""}
        onImportSuccess={handleImportSuccess}
      />

      {/* 指标排序对话框 */}
      <ProjectSelfEvaluationIndicatorSortDialog
        open={sortDialogOpen}
        onClose={handleCloseSortDialog}
        indicators={indicators}
        onSave={handleSaveSort}
        isSaving={isSorting}
      />

      {/* 批量删除确认对话框 */}
      <Dialog open={batchDeleteConfirmOpen} onClose={closeBatchDeleteConfirmDialog}>
        <DialogTitle>确认批量删除</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            确定要删除选中的 <strong>{selectedIndicatorIds.length}</strong> 个项目自评指标吗？
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            此操作不可恢复，请谨慎操作。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBatchDeleteConfirmDialog} disabled={isBatchDeleting}>
            取消
          </Button>
          <Button
            onClick={handleConfirmBatchDelete}
            color="error"
            variant="contained"
            disabled={isBatchDeleting}
          >
            {isBatchDeleting ? "删除中..." : "确认删除"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 得分计算结果对话框 */}
      <Dialog
        open={scoreResultDialogOpen}
        onClose={() => setScoreResultDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AutoAwesomeIcon color="primary" />
            <span>自评得分计算结果</span>
          </Box>
        </DialogTitle>
        <DialogContent>
          {scoreResult && (
            <Box sx={{ py: 2 }}>
              {/* 总分 */}
              <Paper sx={{ p: 3, mb: 3, bgcolor: "primary.50", textAlign: "center" }}>
                <Typography variant="h3" color="primary" sx={{ fontWeight: "bold" }}>
                  {scoreResult.total_score} / {scoreResult.total_max_score}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  自评总分
                </Typography>
              </Paper>

              {/* 各模块得分 */}
              <Grid container spacing={2}>
                {/* 资金执行 */}
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, textAlign: "center" }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      项目资金执行
                    </Typography>
                    <Typography variant="h5" sx={{ my: 1 }}>
                      {scoreResult.fund_execution?.total_score || 0} / {scoreResult.fund_execution?.total_max_score || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      分值
                    </Typography>
                  </Paper>
                </Grid>

                {/* 资金管理 */}
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, textAlign: "center" }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      资金管理情况
                    </Typography>
                    <Typography variant="h5" sx={{ my: 1 }}>
                      {scoreResult.fund_management?.total_score || 0} / {scoreResult.fund_management?.total_max_score || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      分值
                    </Typography>
                  </Paper>
                </Grid>

                {/* 绩效指标 */}
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, textAlign: "center" }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      绩效指标
                    </Typography>
                    <Typography variant="h5" sx={{ my: 1 }}>
                      {scoreResult.indicators?.total_score || 0} / {scoreResult.indicators?.total_max_score || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      分值
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* 绩效指标详情 */}
              {scoreResult.indicators?.by_level1 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
                    绩效指标得分明细
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>一级指标</TableCell>
                          <TableCell align="right">得分</TableCell>
                          <TableCell align="right">满分</TableCell>
                          <TableCell align="right">得分率</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(scoreResult.indicators.by_level1).map(([name, data]: [string, any]) => (
                          <TableRow key={name}>
                            <TableCell>{name}</TableCell>
                            <TableCell align="right">{data.score?.toFixed(2) || 0}</TableCell>
                            <TableCell align="right">{data.max_score || 0}</TableCell>
                            <TableCell align="right">
                              {data.max_score > 0 ? ((data.score / data.max_score) * 100).toFixed(1) + "%" : "0%"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScoreResultDialogOpen(false)}>
            关闭
          </Button>
        </DialogActions>
      </Dialog>

      {/* 文档管理对话框 */}
      <UploadedDocumentsDialog
        open={documentsDialogOpen}
        onClose={() => setDocumentsDialogOpen(false)}
        projectId={projectId?.toString() || ''}
        onUploadComplete={() => {
          fetchIndicators();
          fetchFundManagements();
        }}
      />
    </Box>
  );
};

export default ProjectSelfEvaluationIndicatorManager;

